import React, { useState, useEffect } from 'react';
import { auth, notes, calendar, taskLists } from './api';
import wearService from './wearService';
import './App.css';
import './styles/darkTheme.css';
import './styles/darkThemeAuto.css'; // generated: node scripts/gen-dark-theme.js
import './styles/darkThemeInline.css'; // generated: node scripts/gen-dark-inline.js
import './styles/darkThemeFixes.css'; // hand-written leftovers (loaded last)
import { PushNotifications } from '@capacitor/push-notifications';
import { syncIfDue as syncDeviceCalendarIfDue } from './utils/deviceCalendar';
import { syncIfDue as syncImportedLessonsIfDue } from './utils/importedLessonsSync';
import { LocalNotifications } from '@capacitor/local-notifications';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import { StatusBar, Style } from '@capacitor/status-bar';
import { initializeApp } from 'firebase/app';
import { getMessaging, getToken, onMessage } from 'firebase/messaging';
import CoachView from './components/CoachView';
import PlayerView from './components/PlayerView';
import SavedNotes from './components/SavedNotes';
import AnalyticsDashboard from './components/AnalyticsDashboard';
import ErrorBoundary from './components/ErrorBoundary';
import InstallPrompt from './components/InstallPrompt';
import PushNudge from './components/PushNudge';
import PasswordReset from './components/PasswordReset';
import SettingsPage from './components/Settings/SettingsPage';
import CoachCalendarsCard from './components/Settings/CoachCalendarsCard';
import CoachTasks from './components/Tasks/CoachTasks';
import PlaytomicImportTab from './components/Import/PlaytomicImportTab';
import { pocketEnabledFor } from './utils/features';
import usePullToRefresh from './components/usePullToRefresh';
import HeadcoachTasksCard from './components/Tasks/HeadcoachTasksCard';
import PublicGuestBooking from './pages/PublicGuestBooking';
import CoachInviteSignup from './pages/CoachInviteSignup';
import CoachSelector from './components/CoachSelector';
import { AppStatusProvider, AppStatusContext } from './contexts/AppStatusContext';
import MaintenanceScreen from './components/MaintenanceScreen';
import LoadingSpinner from './components/LoadingSpinner';
import LoadingSkeleton from './components/LoadingSkeleton';
import UpdateBanner from './components/UpdateBanner';
import InAppNotification from './components/InAppNotification';
import SkillUpdateModal from './components/SkillUpdateModal';
import {
  handleNotificationTap,
  peekNavTarget,
  takeNavTarget,
  onNavTarget,
  readNotificationFromUrl
} from './notificationNavigation';

// Inner app component that has access to AppStatusContext
function AppContent() {
  const appStatusContext = React.useContext(AppStatusContext);

  // Make context available globally for API interceptor
  React.useEffect(() => {
    if (appStatusContext) {
      window.__appStatusManager = {
        markServerDown: appStatusContext.markServerDown,
        markServerUp: appStatusContext.markServerUp
      };
    }
  }, [appStatusContext]);

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connectionError, setConnectionError] = useState(null);
  // Tap on the in-app banner: same routing as tapping a system notification
  useEffect(() => {
    const onTap = (e) => handleNotificationTap((e && e.detail) || {});
    window.addEventListener('inAppNotificationTap', onTap);
    return () => window.removeEventListener('inAppNotificationTap', onTap);
  }, []);
  const bgFailures = React.useRef(0); // consecutive failed background refreshes

  // Granular loading states for smooth animations
  // Notes start as loading=true to show skeleton on initial app load
  const [loadingPlayers, setLoadingPlayers] = useState(false);
  const [loadingNotes, setLoadingNotes] = useState(true);  // ✅ Start TRUE to show loading on first tab open
  const [loadingLessons, setLoadingLessons] = useState(false);
  const [loadingSavedNotes, setLoadingSavedNotes] = useState(false);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);
  const refreshCalendarRef = React.useRef(null);
  const [viewingStudent, setViewingStudent] = useState(null);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [showMilestoneCelebration, setShowMilestoneCelebration] = useState(false);
  const [milestoneData, setMilestoneData] = useState(null);
  const [lastShownMilestone, setLastShownMilestone] = useState(() => {
    try {
      return parseInt(localStorage.getItem('lastShownMilestone')) || null;
    } catch {
      return null;
    }
  });
  const [showSkillUpdateModal, setShowSkillUpdateModal] = useState(false);
  const [skillUpdateData, setSkillUpdateData] = useState(null);

  // Auth states
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [authError, setAuthError] = useState('');
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [resetToken, setResetToken] = useState('');
  const [showNotePopup, setShowNotePopup] = useState(false);
  const [popupNote, setPopupNote] = useState(null);
  const [lastSeenNoteId, setLastSeenNoteId] = useState(null);
  const [playerShowSettings, setPlayerShowSettings] = useState(false);

  // 🌐 PUBLIC GUEST BOOKING: State for booking without login
  const [showPublicBooking, setShowPublicBooking] = useState(false);
  const [bookingCoachId, setBookingCoachId] = useState(null);
  const [showCoachSelector, setShowCoachSelector] = useState(false);

  // Coach states
  const [players, setPlayers] = useState([]);
  const [filteredPlayers, setFilteredPlayers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPlayerIndex, setSelectedPlayerIndex] = useState(0);
  const [newNote, setNewNote] = useState('');
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editNoteText, setEditNoteText] = useState('');
  const [newStudentEmail, setNewStudentEmail] = useState('');
  const [showAddStudent, setShowAddStudent] = useState(false);
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [lessonNotes, setLessonNotes] = useState({});
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [lessonNote, setLessonNote] = useState('');
  const [newNoteBadge, setNewNoteBadge] = useState(null);
  const [lessonNotePhotos, setLessonNotePhotos] = useState([]);
  const [editNoteBadge, setEditNoteBadge] = useState(null);
  const [newNoteType, setNewNoteType] = useState(null);
  const [editNoteType, setEditNoteType] = useState(null);

  // Registration states
  const [showRegister, setShowRegister] = useState(false);
  const [registerName, setRegisterName] = useState('');
  const [registerEmail, setRegisterEmail] = useState('');
  const [registerPassword, setRegisterPassword] = useState('');
  const [registerGender, setRegisterGender] = useState('m');
  const [registerHandedness, setRegisterHandedness] = useState('r');
  const [registerError, setRegisterError] = useState('');

  // Player states
  const [myNotes, setMyNotes] = useState([]);
// ================================================================
// ADD TO App.js STATE SECTION (around line 60-75)
// ================================================================

// ✅ ADD THIS STATE FOR STARRED NOTES
const [starredNotes, setStarredNotes] = useState(() => {
  const saved = localStorage.getItem('starredNotes');
  return saved ? JSON.parse(saved) : [];
});

// ✅ ADD THIS FUNCTION TO TOGGLE STAR
const toggleStar = (noteId) => {
  setStarredNotes(prev => {
    const isCurrentlyStarred = prev.includes(noteId);
    const updated = isCurrentlyStarred
      ? prev.filter(id => id !== noteId)  // Remove star
      : [...prev, noteId];                 // Add star

    localStorage.setItem('starredNotes', JSON.stringify(updated));
    return updated;
  });
};

// ✅ CLEANUP STARRED NOTES DISABLED
// Previously this cleanup removed starred note IDs that weren't in myNotes,
// but this incorrectly deleted personal note star status since personal notes
// aren't in myNotes. Starred IDs should persist across note types.
// Note: Users can manually unstar notes they no longer want starred.

  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [modalConfig, setModalConfig] = useState({
    title: '',
    message: '',
    onConfirm: null,
    confirmText: 'Confirm',
    cancelText: 'Cancel',
    showCancel: true
  });

  const [scrollKey, setScrollKey] = useState(0);
  const [activeTab, setActiveTab] = useState('students'); // 'students' or 'saved-notes'
  const [savedNotesCount, setSavedNotesCount] = useState(0);
  const [unansweredMessages, setUnansweredMessages] = useState(0); // coach: head coach tasks waiting for a tick / cross / ?

  // Coach / headcoach pull-down refresh (same gesture as the player view). The scroll area only exists once the coach view is
  // showing, so it is held in state and wrapped in a ref-shaped object that changes when the element appears.
  const [coachScrollEl, setCoachScrollEl] = useState(null);
  const coachScrollRef = React.useMemo(() => ({ current: coachScrollEl }), [coachScrollEl]);
  const [coachContentKey, setCoachContentKey] = useState(0);
  const [coachContentFading, setCoachContentFading] = useState(false);
  const coachReloadRef = React.useRef(async () => {});
  const reloadCoachApp = React.useCallback(() => coachReloadRef.current(), []);
  const coachPull = usePullToRefresh(coachScrollRef, reloadCoachApp, !!coachScrollEl);

  // Format date
  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    const day = date.getDate();
    const month = date.getMonth() + 1;
    const year = date.getFullYear();
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const dayName = dayNames[date.getDay()];
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${day}/${month}/${year} ${dayName} ${hours}:${minutes}:${seconds}`;
  };

  // Set status bar
  useEffect(() => {
    const setStatusBar = async () => {
      try {
        await StatusBar.setStyle({ style: Style.Dark });
        await StatusBar.setBackgroundColor({ color: '#0a0e27' });
      } catch (error) {
      }
    };
    setStatusBar();
  }, []);

  // Register Service Worker for PWA capabilities
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      window.addEventListener('load', async () => {
        try {
          const registration = await navigator.serviceWorker.register('/service-worker.js', {
            scope: '/'
          });

          // Check for updates periodically
          setInterval(() => {
            registration.update();
          }, 60000); // Check every minute

          // Handle updates
          registration.addEventListener('updatefound', () => {
            const newWorker = registration.installing;
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                // Optionally prompt user to update
              }
            });
          });
        } catch (error) {
          console.warn('⚠️ Service Worker registration failed:', error);
        }
      });
    }
  }, []);

  // Initial auth check
  useEffect(() => {
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    if (token && savedUser) {
      setUser(JSON.parse(savedUser));

      // Re-push the token to the watch on every app open, not only right
      // after a fresh login form submit - a phone that stays logged in for
      // days never re-runs handleLogin, so without this the watch's own
      // copy (which the watch needs for its background sync, independent
      // of the phone being open) would only ever get set once and then go
      // stale well before the token's real expiry.
      wearService.syncAuthToken();
    }
    setLoading(false);
  }, []);

  // Check URL for password reset token
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    if (token) {
      setResetToken(token);
      setShowPasswordReset(true);
      // Note: Keep token in URL to allow page refreshes during reset
    }
  }, []);

  // Check URL for Google Calendar connect result (redirected here by the
  // backend's OAuth callback after the coach approves/denies on Google's side)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const gcal = params.get('gcal');
    if (gcal === 'connected') {
      showAlertModal('Google Calendar', '✅ Your Google Calendar is now connected.');
    } else if (gcal === 'error') {
      showAlertModal('Google Calendar', '❌ Could not connect your Google Calendar. Please try again.');
    }
    if (gcal) {
      params.delete('gcal');
      const newSearch = params.toString();
      window.history.replaceState({}, '', window.location.pathname + (newSearch ? `?${newSearch}` : ''));
    }
  }, []);

  // Check watch interface on app startup
  useEffect(() => {
    const interfaceAvailable = wearService.debugWatchInterface();
    if (!interfaceAvailable) {
      console.warn('⚠️ Watch interface not available - app may not be running on Android Wear');
    }
  }, []);

  // Load data when user logs in
  useEffect(() => {
    if (user) {
      if (user.role === 'coach' || user.role === 'headcoach') {
        loadPlayers();
        loadLessonNotes();
        // 📅 Sync buffer lessons from calendar on app open
        syncBufferLessons();
      } else if (user.role === 'player' || user.role === 'manager') {
        // Both players and managers load their own notes
        loadMyNotes();
      }
    }
  }, [user]);

  // Filter players
  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredPlayers(players);
    } else {
      const filtered = players.filter(player =>
        player.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        player.email.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredPlayers(filtered);
    }
  }, [searchQuery, players]);

  // Push notifications for all users (players, managers, coaches, and headcoaches)
  useEffect(() => {
    if (user && (user.role === 'player' || user.role === 'manager' || user.role === 'coach' || user.role === 'headcoach')) {
      try {
        registerForPushNotifications();
      } catch (error) {
        console.error('Failed to register push notifications:', error);
      }
    }
  }, [user]);

  // Pull-to-refresh (PlayerView) asks for the notes to be loaded again without reloading the page
  useEffect(() => {
    const onRefreshData = () => { try { loadMyNotes(); } catch (err) { /* the views fetch their own data too */ } };
    window.addEventListener('cpRefreshData', onRefreshData);
    return () => window.removeEventListener('cpRefreshData', onRefreshData);
  });

  // iPhone calendar (coaches who linked it in Settings): refresh the copy managers/headcoach read, on open and whenever the app returns
  useEffect(() => {
    if (!user || (user.role !== 'coach' && user.role !== 'headcoach')) return undefined;
    syncDeviceCalendarIfDue(true);
    syncImportedLessonsIfDue(true); // lessons imported from Playtomic -> this iPhone's calendar (only when the coach turned it on)
    const onVisible = () => { if (document.visibilityState === 'visible') { syncDeviceCalendarIfDue(); syncImportedLessonsIfDue(); } };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [user?._id, user?.role]);

  // Coach: badge on the Tasks tab = head coach tasks still waiting for an answer
  useEffect(() => {
    if (!user || user.role !== 'coach') return undefined;
    const load = () => taskLists.getUnansweredCount().then(res => setUnansweredMessages(res.data.count || 0)).catch(() => {});
    load();
    const timer = setInterval(() => { if (!document.hidden) load(); }, 60 * 1000);
    return () => clearInterval(timer);
  }, [user?._id, user?.role]);

  // Watch message handler for date-specific lesson requests
  useEffect(() => {
    if (typeof window !== 'undefined') {
      // Handler for watch requesting lessons for a specific date
      // Called by MainActivity when watch sends /request-lessons message
      window.fetchLessonsForDate = async (dateString) => {
        try {
          const requestedDate = dateString || new Date().toISOString().split('T')[0];

          // Parse the date string (format: YYYY-MM-DD from watch)
          const [year, month, day] = requestedDate.split('-').map(Number);
          if (!year || !month || !day) {
            console.warn('⚠️ Invalid date format received:', requestedDate);
            return { status: 'error', message: 'Invalid date format' };
          }

          // Create Date object for this specific date
          const lessonDate = new Date(year, month - 1, day);

          // Fetch lessons from calendar API for the requested date
          const response = await calendar.getEventsForDate(lessonDate);
          const lessons = response.data.events || [];

          // Send lessons back to watch (with date validation metadata)
          const result = await wearService.sendLessonsToWatch(lessons, {});

          if (result) {
            return {
              status: 'success',
              lessonsCount: lessons.length,
              date: requestedDate
            };
          } else {
            console.warn('⚠️ Failed to send lessons to watch');
            return { status: 'error', message: 'Failed to send to watch' };
          }
        } catch (error) {
          console.error('❌ Error fetching lessons for watch date:', error);
          return {
            status: 'error',
            message: error.message
          };
        }
      };

    }
  }, []);

  // Auto-refresh for players and managers - silently in background
  useEffect(() => {
    if (user && (user.role === 'player' || user.role === 'manager')) {
      const interval = setInterval(() => {
        // Refresh silently without showing loading animation
        loadMyNotes(false, false);
      }, 10000);
      // Right after unlocking the phone the network is often not back yet: retry shortly after the app
      // comes back to the foreground or the connection returns, instead of leaving an error on screen.
      const retry = () => {
        if (document.visibilityState === 'hidden') return;
        setTimeout(() => loadMyNotes(false, false), 1200);
        setTimeout(() => loadMyNotes(false, false), 4000);
      };
      document.addEventListener('visibilitychange', retry);
      window.addEventListener('online', retry);
      window.addEventListener('focus', retry);
      return () => {
        clearInterval(interval);
        document.removeEventListener('visibilitychange', retry);
        window.removeEventListener('online', retry);
        window.removeEventListener('focus', retry);
      };
    }
  }, [user]);

  const registerForPushNotifications = async () => {
    try {
      if (!PushNotifications) return;

      // Create channels for different notification types
      await PushNotifications.createChannel({
        id: 'player_notes_channel',
        name: 'Player Notes',
        description: 'Notifications for new notes from coach',
        importance: 5,
        visibility: 1,
        sound: 'default',
        vibration: true
      }).catch(err => {});

      // Coaching-specific channel with high priority
      await PushNotifications.createChannel({
        id: 'coaching_channel',
        name: 'Coaching Notifications',
        description: 'Notifications for bookings and questions',
        importance: 5,
        visibility: 1,
        sound: 'default',
        vibration: true
      }).catch(err => {});

      // Check permissions and setup listeners
      try {
        let permStatus = await PushNotifications.checkPermissions();
        if (permStatus.receive === 'prompt') {
          permStatus = await PushNotifications.requestPermissions();
        }
        if (permStatus.receive !== 'granted') return;

        PushNotifications.addListener('registration', async (token) => {
          try {
            await notes.saveFcmToken(token.value);
          } catch (error) {
            console.error('Error saving FCM token:', error);
          }
        });
        PushNotifications.addListener('registrationError', (err) => {
          console.error('Push registration failed:', err);
        });
      } catch (permError) {
        // Native push notifications not available (e.g., on web/browser)
        // Try web FCM as fallback
        if (permError.message?.includes('not implemented')) {
          try {
            await registerWebPushNotifications();
          } catch (webError) {
          }
        } else {
          console.error('Permission check error:', permError);
        }
        return;
      }

      // Handle foreground notifications (app is open)
      PushNotifications.addListener('pushNotificationReceived', async (notification) => {
        const notificationType = notification?.data?.type;
        const title = notification?.title || 'New Notification';
        const body = notification?.body || '';

        // ✅ PLAY SOUND AND HAPTICS
        try {
          // Haptic feedback
          await Haptics.impact({ style: ImpactStyle.Medium });

          // Play system notification sound
          if ('Notification' in window) {
            const audio = new Audio('/notification-sound.mp3');
            audio.play().catch(() => console.log('Could not play notification sound'));
          }
        } catch (err) {
        }

        // Visual notice: our own banner, on top of everything (see components/InAppNotification.js)
        window.dispatchEvent(new CustomEvent('inAppNotification', { detail: { title, body, data: notification?.data || {} } }));

        // Handle specific notification types
        if (notificationType) {
          if (notificationType.includes('booking')) {
            // For booking notifications, show toast
          } else if (notificationType === 'player_update') {
            // ✅ FOR PLAYER UPDATES (questions, matches, thoughts)
            const updateType = notification?.data?.updateType;
            let updateTypeLabel = 'Update';
            if (updateType === 'question') updateTypeLabel = 'Question';
            else if (updateType === 'match') updateTypeLabel = 'Match';
            else if (updateType === 'thought') updateTypeLabel = 'Thought';
            // Reload notes if player
            if (user?.role === 'player' || user?.role === 'manager') {
              try {
                loadMyNotes();
              } catch (err) {
              }
            }

          } else if (notificationType.includes('question')) {
            // For question notifications, show toast
          } else if (notificationType === 'lesson_reminder') {
            // ✅ For lesson reminder notifications, show toast
            // Reminder already displayed as local notification
          } else if (notificationType === 'skill_update') {
            // ✅ For skill update notifications, show modal
            const skillId = notification?.data?.skillId;
            const oldRating = parseInt(notification?.data?.oldRating) || 0;
            const newRating = parseInt(notification?.data?.newRating) || 0;
            const change = parseInt(notification?.data?.change) || 0;
            const coachName = notification?.data?.coachName || 'Your coach';

            setSkillUpdateData({
              skillId,
              oldRating,
              newRating,
              change,
              coachName,
              timestamp: new Date()
            });
            setShowSkillUpdateModal(true);

            // Auto-close modal after 6 seconds
            setTimeout(() => setShowSkillUpdateModal(false), 6000);
          } else if (notificationType.includes('photo')) {
            // ✅ For photo note notifications, reload notes
            if (user?.role === 'player' || user?.role === 'manager') {
              try {
                loadMyNotes();
              } catch (err) {
              }
            }
          } else {
            // For other notifications (like notes), reload notes
            if (user?.role === 'player' || user?.role === 'manager') {
              loadMyNotes();
            }
          }
        } else {
        }
      });

      // Notification taps (app in background / closed): land on the right
      // tab + section. All routing lives in notificationNavigation.js.
      PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
        handleNotificationTap(action?.notification?.data || {});
      });

      // Taps on the local notification we show while the app is in the foreground
      LocalNotifications.addListener('localNotificationActionPerformed', (action) => {
        handleNotificationTap(action?.notification?.extra || {});
      });

      await PushNotifications.register();
    } catch (error) {
      console.error('Push notification setup failed:', error);
    }
  };

  const registerWebPushNotifications = async () => {
    try {
      // Firebase config - must be set via environment variables in production
      const firebaseConfig = {
        apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
        authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
        projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
        storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
        messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
        appId: process.env.REACT_APP_FIREBASE_APP_ID
      };

      // Validate all required Firebase config is present
      if (!Object.values(firebaseConfig).every(val => val)) {
        throw new Error('Missing Firebase configuration - check environment variables');
      }

      // Register Firebase messaging service worker FIRST
      if ('serviceWorker' in navigator) {
        try {
          await navigator.serviceWorker.register('/firebase-messaging-sw.js');
        } catch (err) {
          // Service worker registration not critical
        }
      }

      // Initialize Firebase
      const app = initializeApp(firebaseConfig);
      const messaging = getMessaging(app);

      // Skip permission request here - will be requested on user interaction (login/signup)
      // Notification.requestPermission() can only be called in response to user interaction

      // Get FCM token for this web client (silent, no permission needed yet)
      try {
        const vapidKey = process.env.REACT_APP_FIREBASE_VAPID_KEY;
        if (!vapidKey) {
          throw new Error('Missing REACT_APP_FIREBASE_VAPID_KEY environment variable');
        }
        const token = await getToken(messaging, {
          vapidKey: vapidKey
        }).catch(() => {
          // Token may not be available if permission not granted - that's OK
          return null;
        });

        if (token) {
          // Store globally so it can be registered after login
          window.fcmToken = token;

          // If user is already logged in, register token immediately
          const storedUser = localStorage.getItem('user');
          if (storedUser) {
            try {
              await notes.saveFcmToken(token);
            } catch (error) {
              console.warn('⚠️ Error saving web FCM token:', error.message);
            }
          } else {
          }
        } else {
          console.warn('⚠️ getToken() returned null');
        }
      } catch (err) {
        console.error('❌ Error getting FCM token:', err);
        console.error('   Error name:', err.name);
        console.error('   Error message:', err.message);
        console.error('   Error code:', err.code);
      }

      // Handle foreground messages (app is in focus)
      onMessage(messaging, (payload) => {

        const notificationTitle = payload.notification?.title || 'CoachPad';
        window.dispatchEvent(new CustomEvent('inAppNotification', { detail: { title: notificationTitle, body: payload.notification?.body || '', data: payload.data || {} } }));

        // Play notification sound
        try {
          const audio = new Audio('/notification-sound.mp3');
          audio.play().catch(() => console.log('Could not play notification sound'));
        } catch (err) {
        }

        // Handle specific notification types
        const notificationType = payload.data?.type;
        if (notificationType) {
          if (notificationType.includes('question')) {
            if (user?.role === 'coach' || user?.role === 'headcoach') {
              // Reload notes for coaches when they get Q&A notifications
              try {
                loadMyNotes();
              } catch (err) {
              }
            }
          } else if (notificationType === 'player_update') {
            // Reload notes for players on updates
            if (user?.role === 'player' || user?.role === 'manager') {
              try {
                loadMyNotes();
              } catch (err) {
              }
            }
          }
        }
      });

      // Initialize service worker with Firebase config for background messages
      if ('serviceWorker' in navigator) {
        try {
          const registration = await navigator.serviceWorker.ready;
          registration.controller?.postMessage({
            type: 'INIT_FIREBASE',
            config: firebaseConfig
          });
        } catch (err) {
        }
      }

    } catch (error) {
      // FCM setup optional - gracefully fail if Firebase config missing
    }
  };

  const showAlertModal = (title, message) => {
    setModalConfig({
      title,
      message,
      onConfirm: () => setShowModal(false),
      confirmText: 'OK',
      cancelText: '',
      showCancel: false
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedLesson(null);
    setLessonNote('');
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setAuthError('');
    try {
      const response = await auth.login({ email, password });
      const { token, user: userData } = response.data;
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);
      setEmail('');
      setPassword('');

      // ✅ Sync auth token to watch app for independent fetching
      await wearService.syncAuthToken();

      // ✅ Request notification permission (can only be done after user interaction)
      try {
        if ('Notification' in window && Notification.permission === 'default') {
          const permission = await Notification.requestPermission();
          if (permission === 'granted') {
            // Permission granted - retry getting FCM token
            try {
              const messaging = getMessaging();
              const vapidKey = process.env.REACT_APP_FIREBASE_VAPID_KEY;
              if (vapidKey) {
                const token = await getToken(messaging, { vapidKey });
                if (token) {
                  window.fcmToken = token;
                }
              }
            } catch (tokenErr) {
              // Token retry failed - continue anyway
            }
          }
        }
      } catch (err) {
        // Permission request failed - continue anyway
      }

      // ✅ Register FCM token after login for push notifications
      try {
        if (window.fcmToken) {
          await notes.saveFcmToken(window.fcmToken);
        }
      } catch (fcmError) {
        // Don't fail login if FCM registration fails
      }
    } catch (error) {
      setAuthError(error.response?.data?.message || 'Login failed');
    }
  };

  const handlePlayerRegister = async (e) => {
    e.preventDefault();
    setRegisterError('');
    try {
      await auth.register({
        name: registerName,
        email: registerEmail,
        password: registerPassword,
        role: 'player',
        gender: registerGender,
        handedness: registerHandedness
      });
      showAlertModal('Success', 'Registration successful! You can now login.');
      setShowRegister(false);
      setRegisterName('');
      setRegisterEmail('');
      setRegisterPassword('');
      setRegisterGender('m');
      setRegisterHandedness('r');
    } catch (error) {
      setRegisterError(error.response?.data?.message || 'Registration failed');
    }
  };

  // Handle pull-to-refresh for all tabs
  const handleRefresh = async () => {
    try {

      // Reload current view based on user role
      if (user.role === 'coach') {
        // Refresh students and notes for coaches
        await loadPlayers();
      } else if (user.role === 'player' || user.role === 'manager') {
        // Refresh notes for players
        await loadMyNotes();
      }

      // Optional: Trigger haptic feedback on refresh complete
      try {
        await Haptics.impact({ style: ImpactStyle.Light });
      } catch (hapticErr) {
        // Haptics may fail on web/non-capacitor environments
      }
    } catch (error) {
      console.error('❌ Refresh error:', error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setPlayers([]);
    setMyNotes([]);
    setSelectedPlayerIndex(0);
  };

  const loadPlayers = async () => {
    try {
      setLoadingPlayers(true);
      const response = await notes.getAllPlayers();
      setPlayers(response.data);
      setFilteredPlayers(response.data);
    } catch (error) {
      console.error('Error loading players:', error);
    } finally {
      setLoadingPlayers(false);
    }
  };

  const refreshPlayersAfterDrop = async () => {
    try {
      const response = await notes.getAllPlayers();
      setPlayers(response.data);
      setFilteredPlayers(response.data);
    } catch (error) {
      console.error('Error refreshing players:', error);
    }
  };

  // A Pocket note assigned to a student materializes as a real note on
  // that student server-side, but the coach's already-loaded `players`
  // (and, if they have that same student open, `viewingStudent`) are
  // stale snapshots that won't include it until refetched - same reason
  // CoachView's own add/edit/delete-note handlers refetch and re-set
  // viewingStudent after every mutation.
  const refreshPlayersAfterPocketAssignment = async () => {
    try {
      const response = await notes.getAllPlayers();
      setPlayers(response.data);
      setFilteredPlayers(response.data);
      if (viewingStudent) {
        const freshStudent = response.data.find(p => p._id === viewingStudent._id);
        if (freshStudent) {
          setViewingStudent(freshStudent);
        }
      }
    } catch (error) {
      console.error('Error refreshing players after pocket note assignment:', error);
    }
  };

  const loadMyNotes = async (forceReload = false, showLoadingAnimation = true) => {
    try {
      // Only show loading animation for manual/initial loads, not auto-refresh
      if (showLoadingAnimation) {
        setLoadingNotes(true);
      }
      const response = await notes.getMyNotes();
      let notesData = [];
      if (response.data) {
        if (Array.isArray(response.data)) {
          notesData = response.data;
        } else if (response.data.notes && Array.isArray(response.data.notes)) {
          notesData = response.data.notes;
        } else if (response.data.name && response.data.notes) {
          notesData = response.data.notes;
        }
      }

      if (notesData.length > 0) {
        const latestNote = notesData.sort((a, b) =>
          new Date(b.createdAt) - new Date(a.createdAt)
        )[0];

        if (forceReload && lastSeenNoteId && latestNote._id !== lastSeenNoteId) {
          setPopupNote(latestNote);
          setShowNotePopup(true);
        }
        // ❌ REMOVED: setLastSeenNoteId should ONLY be set in NotePopup onClose handler
        // This prevents duplicate modal rendering caused by continuous state updates
      }

      try {
        const milestoneResponse = await notes.checkMilestone();
        if (milestoneResponse.data.shouldCelebrate) {
          const milestone = milestoneResponse.data.milestoneData?.milestone;
          // Only show popup if this is a new milestone (not already shown)
          if (milestone && milestone !== lastShownMilestone) {
            setMilestoneData(milestoneResponse.data.milestoneData);
            setShowMilestoneCelebration(true);
            setLastShownMilestone(milestone);
            localStorage.setItem('lastShownMilestone', milestone);
          }
        }
      } catch (milestoneError) {
        console.error('Error checking milestone:', milestoneError);
      }

      setMyNotes(notesData);
      setLoading(false);
      bgFailures.current = 0;
      setConnectionError(null);
    } catch (error) {
      // A silent background refresh that fails (e.g. the network is still waking up after the phone was
      // unlocked) must not wipe the notes or show an error right away - only after several failures in a row.
      if (!showLoadingAnimation && !forceReload) {
        bgFailures.current += 1;
        if (bgFailures.current < 3) { setLoading(false); return; }
      }
      console.error('ERROR LOADING NOTES', error);

      // Provide helpful error messages
      let errorMsg = 'Failed to load notes';
      if (error.code === 'ECONNABORTED') {
        errorMsg = 'Connection timeout - backend may be down';
      } else if (error.response?.status === 503) {
        errorMsg = 'Backend service unavailable (503)';
      } else if (error.response?.status === 404) {
        errorMsg = 'API endpoint not found';
      } else if (!error.response) {
        errorMsg = 'Cannot connect to backend - check your connection';
      }

      setConnectionError(errorMsg);
      if (showLoadingAnimation || forceReload) setMyNotes([]); // keep what is on screen when only a background refresh failed
      setLoading(false);
    } finally {
      setLoadingNotes(false);
    }
  };

  const loadLessonNotes = async () => {
    try {
      const response = await notes.getLessonNotes();
      const notesObj = {};
      response.data.forEach((note) => {
        // Store ENTIRE note object (including attachments, performanceBadge, etc.)
        // Don't just store note.note - we need the full structure
        notesObj[note.lessonId] = {
          _id: note._id,
          text: note.note,
          note: note.note,
          performanceBadge: note.performanceBadge,
          noteType: note.noteType,
          attachments: note.attachments || [],
          lessonTitle: note.lessonTitle,
          lessonEndTime: note.lessonEndTime,
          createdAt: note.createdAt,
          updatedAt: note.updatedAt
        };
      });
      setLessonNotes(notesObj);
    } catch (error) {
      console.error('Error loading lesson notes:', error);
    }
  };

  // 📅 Sync buffer lessons from Google Calendar
  const syncBufferLessons = async () => {
    try {
      const { bookings } = await import('./api');
      const response = await bookings.syncBufferSlots();

      const { created, cleaned, skipped, errors } = response.data.result;

      // Show notification if buffers were found and created
      if (created > 0) {
        setShowSuccessToast(true);
        setTimeout(() => setShowSuccessToast(false), 4000);
      }

      // Return response for caller to use
      return response;
    } catch (error) {
      console.warn('⚠️ Buffer sync error (non-critical):', error.message);
      // Throw error so caller can handle it
      throw error;
    }
  };

  const handleRemoveStudent = (playerId, playerName) => {
    setModalConfig({
      title: 'Remove Student',
      message: `Remove ${playerName} from your students list? Their account will not be deleted.`,
      onConfirm: async () => {
        try {
          await notes.removeStudent(playerId);
          await loadPlayers();
          setSelectedPlayerIndex(0);
          showAlertModal('Success', 'Student removed from your list');
        } catch (error) {
          showAlertModal('Error', error.response?.data?.message || 'Failed to remove student');
        }
      },
      confirmText: 'Remove',
      cancelText: 'Cancel',
      showCancel: true
    });
    setShowModal(true);
  };

  const handleAddStudent = async () => {
    if (!newStudentEmail.trim()) return;
    try {
      await notes.addStudent(newStudentEmail);
      showAlertModal('Success', 'Student added successfully!');
      setNewStudentEmail('');
      setShowAddStudent(false);
      setSearchSuggestions([]);
      setShowSuggestions(false);
      loadPlayers();
    } catch (error) {
      showAlertModal('Error', error.response?.data?.message || 'Failed to add student');
    }
  };

  const handleSearchPlayers = async (query) => {
    setNewStudentEmail(query);
    if (query.length < 2) {
      setSearchSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    try {
      const response = await notes.searchPlayers(query);
      setSearchSuggestions(response.data);
      setShowSuggestions(response.data.length > 0);
    } catch (error) {
      console.error('Error searching players:', error);
    }
  };

const [readNotes, setReadNotes] = useState(() => {
    const saved = localStorage.getItem('readNotes');
    return saved ? JSON.parse(saved) : [];
  });

  const markAsRead = (noteId) => {
    setReadNotes(prev => {
      const updated = [...prev, noteId];
      localStorage.setItem('readNotes', JSON.stringify(updated));
      return updated;
    });
  };

  const handleSelectSuggestion = (player) => {
    setNewStudentEmail(player.email);
    setShowSuggestions(false);
    setSearchSuggestions([]);
  };

  const handleStartEdit = (note) => {
    setEditingNoteId(note._id);
    setEditNoteText(note.text);
  };

  const handleCancelEdit = () => {
    setEditingNoteId(null);
    setEditNoteText('');
  };

  const handleLessonClick = async (lesson) => {
    try {
      const response = await notes.getLessonNotes();
      const notesObj = {};
      response.data.forEach(note => {
        // Store ENTIRE note object including attachments
        notesObj[note.lessonId] = {
          _id: note._id,
          text: note.note,
          note: note.note,
          performanceBadge: note.performanceBadge,
          noteType: note.noteType,
          attachments: note.attachments || [],
          lessonTitle: note.lessonTitle,
          lessonEndTime: note.lessonEndTime,
          createdAt: note.createdAt,
          updatedAt: note.updatedAt
        };
      });
      setLessonNotes(notesObj);

      const existingNoteObj = notesObj[lesson.id];
      const existingNote = existingNoteObj?.text || '';
      const existingPhotos = existingNoteObj?.attachments || [];

      // Convert existing attachments to PhotoUploadWidget format for preview
      const photosForPreview = existingPhotos.map((attachment, idx) => ({
        id: attachment._id || `attachment_${idx}`,
        file: null, // No file object for existing attachments
        caption: attachment.caption || '',
        preview: attachment.cloudinaryUrl,
        fileName: attachment.originalFileName,
        uploadStatus: 'completed',
        cloudinaryUrl: attachment.cloudinaryUrl,
        thumbnailUrl: attachment.thumbnailUrl
      }));

      setSelectedLesson(lesson);
      setLessonNote(existingNote);
      setLessonNotePhotos(photosForPreview);
      setShowModal(true);
      setModalConfig({
        title: existingNote ? `Edit Note: ${lesson.summary}` : `Add Note: ${lesson.summary}`,
        message: '',
        onConfirm: null,
        confirmText: existingNote ? 'Update Note' : 'Save Note',
        cancelText: 'Cancel',
        showCancel: true
      });
    } catch (error) {
      console.error('Error loading lesson notes:', error);
    }
  };

  // ================================================================
// UPDATED handleSaveLessonNote IN App.js
// Replace the existing function (around line 445)
// ================================================================

const handleSaveLessonNote = async () => {
  if (!lessonNote?.trim()) {
    alert('Please enter a note!');
    return;
  }
  if (!selectedLesson) {
    alert('No lesson selected!');
    return;
  }

  try {
    const lessonEndTime = selectedLesson.end
      ? new Date(selectedLesson.end.dateTime)
      : new Date(selectedLesson.start.dateTime);

    // ✅ AUTO-SET noteType to 'lesson' for all lesson notes
    const saveResponse = await notes.saveLessonNote(
      selectedLesson.id,
      selectedLesson.summary,
      lessonNote,
      lessonEndTime,
      newNoteBadge,      // ← Pass the badge
      'lesson'           // ← Auto-set to 'lesson'
    );

    const savedNote = saveResponse?.data;

    // ✅ Upload photos if any were selected
    let photosUploaded = 0;
    let photoErrors = [];

    if (lessonNotePhotos && lessonNotePhotos.length > 0 && savedNote?._id) {

      for (const photoData of lessonNotePhotos) {
        // Skip if it's already a completed attachment (no file object)
        if (!photoData.file) {
          console.log(`⏭️  Skipping existing attachment: ${photoData.fileName} (already uploaded)`);
          continue;
        }

        try {
          // photoData is { file, caption, preview, fileName, ... }
          // Extract the actual File object and caption
          console.log(`📤 Uploading photo: ${photoData.fileName} (${(photoData.file.size / 1024 / 1024).toFixed(2)}MB)`);
          const uploadResult = await notes.uploadPhoto(savedNote._id, photoData.file, photoData.caption, user?.id);
          photosUploaded++;
        } catch (photoError) {
          console.error(`❌ Error uploading photo ${photoData.fileName}:`, photoError);
          photoErrors.push(`${photoData.fileName}: ${photoError.message}`);
          // Don't fail the entire save if one photo fails
        }
      }

    } else {
      console.log(`ℹ️  No photos to upload (${lessonNotePhotos?.length || 0} in state)`);
    }

    // IMPORTANT: Reload lesson notes AFTER all photos are uploaded
    // This ensures we get the complete note with all attachments
    await loadLessonNotes();
    if (refreshCalendarRef.current) {
      await refreshCalendarRef.current();
    }

    // Show success message
    setShowSuccessToast(true);
    setTimeout(() => setShowSuccessToast(false), 2000);

    // Show alert with success message
    const successMsg = photosUploaded > 0
      ? `Lesson note saved! ${photosUploaded} photo${photosUploaded > 1 ? 's' : ''} uploaded successfully`
      : 'Lesson note saved!';

    showAlertModal('Success', successMsg);

    // Close modal and reset state AFTER a brief delay (allows user to see success)
    setTimeout(() => {
      setSelectedLesson(null);
      setLessonNote('');
      setNewNoteBadge(null);  // ← Reset badge
      setLessonNotePhotos([]);  // ← Reset photos
      setShowModal(false);
    }, 500);
  } catch (error) {
    console.error('ERROR saving lesson note', error);
    setShowModal(false);
    showAlertModal('Error', 'Failed to save');
  }
};

  // Notification tap entry points that aren't native: web push (service worker
  // message / ?notification= URL) - and the App-level part of any tap (coach
  // account tabs and opening the student a notification is about).
  useEffect(() => {
    const fromUrl = readNotificationFromUrl();
    if (fromUrl) handleNotificationTap(fromUrl);

    const onSwMessage = (event) => {
      if (event.data && event.data.type === 'NOTIFICATION_CLICK') {
        handleNotificationTap(event.data.data || {});
      }
    };
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', onSwMessage);
    }
    return () => {
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('message', onSwMessage);
      }
    };
  }, []);

  // A player who has the Settings page open is not inside PlayerView, so a notification tap would have nothing
  // to react to. Leave Settings first; PlayerView then mounts and picks up the pending target itself.
  useEffect(() => {
    const leaveSettingsForPlayerTarget = () => {
      const target = peekNavTarget();
      if (target && target.role === 'player') setPlayerShowSettings(false);
    };
    leaveSettingsForPlayerTarget();
    return onNavTarget(leaveSettingsForPlayerTarget);
  }, []);

  useEffect(() => {
    const applyAppLevelTarget = () => {
      const target = peekNavTarget();
      if (!target || target.role !== 'coach') return;

      if (target.appTab) {
        takeNavTarget('coach');
        setActiveTab(target.appTab);
        // Lesson briefing pointing at one unassigned Pocket summary: the
        // Pocket notes section opens and scrolls to that exact summary
        if (target.pocketNoteId) {
          sessionStorage.setItem('focusPocketNote', target.pocketNoteId);
          window.dispatchEvent(new Event('focusPocketNote'));
        }
        // A head coach task list: the Tasks tab opens it in a dialog
        if (target.listId && target.appTab === 'messages') {
          sessionStorage.setItem('focusTaskList', target.listId);
          window.dispatchEvent(new Event('focusTaskList'));
        }
        return;
      }

      // Leave settings / saved notes / analytics so CoachView (its own tabs) shows;
      // CoachView itself then picks the tab / section / student.
      setActiveTab('students');
    };
    applyAppLevelTarget();
    return onNavTarget(applyAppLevelTarget);
  }, [user?.role]);

  const handleViewStudent = (student) => {
    setViewingStudent(student);
  };

  // Open the student a lesson-briefing notification pointed at. CoachView then
  // scrolls to / highlights the specific note (see its focusStudentNote effect).
  useEffect(() => {
    const openFocusedStudent = () => {
      try {
        const raw = sessionStorage.getItem('focusStudentNote');
        if (!raw || !players || players.length === 0) return;
        const { playerId } = JSON.parse(raw);
        const student = players.find(p => p._id === playerId);
        if (student && viewingStudent?._id !== student._id) setViewingStudent(student);
      } catch (e) {
        // ignore malformed context
      }
    };
    openFocusedStudent();
    window.addEventListener('focusStudentNote', openFocusedStudent);
    return () => window.removeEventListener('focusStudentNote', openFocusedStudent);
  }, [players, viewingStudent?._id]);

  const handleBackToList = () => {
    setViewingStudent(null);
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  // 🌐 PUBLIC GUEST BOOKING: Check for /book/:coachId URL pattern
  const pathname = window.location.pathname;
  const bookingMatch = pathname.match(/\/book\/([a-zA-Z0-9]+)/);
  if (bookingMatch) {
    const coachId = bookingMatch[1];
    return <PublicGuestBooking coachId={coachId} onClose={() => window.history.back()} />;
  }

  // Headcoach-only invite link: the only path that can create a coach account
  const coachInviteMatch = pathname.match(/\/coach-invite\/([a-zA-Z0-9]+)/);
  if (coachInviteMatch) {
    return <CoachInviteSignup token={coachInviteMatch[1]} />;
  }

  // 🌐 PUBLIC GUEST BOOKING: Show public booking if user clicked "Book Without Login"
  if (showPublicBooking && bookingCoachId) {
    return <PublicGuestBooking coachId={bookingCoachId} onClose={() => setShowPublicBooking(false)} />;
  }

  // 🌐 PUBLIC GUEST BOOKING: Show coach selector
  if (showCoachSelector) {
    return <CoachSelector
      onSelectCoach={(coachId) => {
        setBookingCoachId(coachId);
        setShowCoachSelector(false);
        setShowPublicBooking(true);
      }}
      onClose={() => setShowCoachSelector(false)}
    />;
  }

  // Login screen
  if (!user) {
    // Show password reset if user clicked "Forgot Password" or has token in URL
    if (showPasswordReset) {
      return <PasswordReset onBackToLogin={() => setShowPasswordReset(false)} prefilledToken={resetToken} />;
    }

    return (
      <div className="App">
        <div className="login-container">
          <div className="login-box">
            <h1>CoachPad</h1>
            {!showRegister ? (
              <form onSubmit={handleLogin}>
                <input type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} required />
                <input type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} required />
                {authError && <div className="error">{authError}</div>}
                <button type="submit">Login</button>
                <div style={{ textAlign: 'center', marginTop: '15px' }}>
                  <button
                    type="button"
                    onClick={() => setShowPasswordReset(true)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#2f4ba2',
                      cursor: 'pointer',
                      textDecoration: 'underline',
                      fontSize: '12px',
                      padding: '5px',
                      marginBottom: '10px'
                    }}
                  >
                    🔐 Forgot Password?
                  </button>
                </div>

                {/* 🌐 PUBLIC GUEST BOOKING: Book without login button */}
                <div style={{ textAlign: 'center', marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #ddd' }}>
                  <div style={{ fontSize: '12px', color: '#999', marginBottom: '10px' }}>Or book a lesson without an account</div>
                  <button
                    type="button"
                    onClick={() => setShowCoachSelector(true)}
                    style={{ background: '#2f4ba2', marginBottom: '10px', width: '100%', padding: '12px' }}
                  >
                    🎾 Book a Lesson
                  </button>
                </div>

                <div style={{ textAlign: 'center', fontSize: '12px', marginTop: '6px' }}>
                  <a href="/privacy.html" target="_blank" rel="noopener noreferrer" style={{ color: '#2f4ba2' }}>Privacy policy</a>
                  {' · '}
                  <a href="/delete-account.html" target="_blank" rel="noopener noreferrer" style={{ color: '#2f4ba2' }}>Delete account</a>
                </div>

                <div style={{ textAlign: 'center' }}>
                  <button type="button" onClick={() => setShowRegister(true)} style={{ background: '#27ae60' }}>
                    Create Player Account
                  </button>
                </div>
              </form>
            ) : (
              <form onSubmit={handlePlayerRegister}>
                <h2 style={{ fontSize: '18px', marginBottom: '15px' }}>Player Registration</h2>
                <input type="text" placeholder="Your Name" value={registerName} onChange={(e) => setRegisterName(e.target.value)} required />
                <input type="email" placeholder="Email" value={registerEmail} onChange={(e) => setRegisterEmail(e.target.value)} required />
                <input type="password" placeholder="Password" value={registerPassword} onChange={(e) => setRegisterPassword(e.target.value)} required />

                {/* Gender Selection */}
                <div style={{ marginBottom: '10px' }}>
                  <div style={{ fontSize: '12px', color: '#666', marginBottom: '5px', fontWeight: '600' }}>Gender</div>
                  <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer' }}>
                      <input
                        type="radio"
                        name="gender"
                        value="m"
                        checked={registerGender === 'm'}
                        onChange={(e) => setRegisterGender(e.target.value)}
                        style={{ cursor: 'pointer' }}
                      />
                      <span>Male ♂️</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer' }}>
                      <input
                        type="radio"
                        name="gender"
                        value="f"
                        checked={registerGender === 'f'}
                        onChange={(e) => setRegisterGender(e.target.value)}
                        style={{ cursor: 'pointer' }}
                      />
                      <span>Female ♀️</span>
                    </label>
                  </div>
                </div>

                {/* Handedness Selection */}
                <div style={{ marginBottom: '15px' }}>
                  <div style={{ fontSize: '12px', color: '#666', marginBottom: '5px', fontWeight: '600' }}>Handedness</div>
                  <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer' }}>
                      <input
                        type="radio"
                        name="handedness"
                        value="r"
                        checked={registerHandedness === 'r'}
                        onChange={(e) => setRegisterHandedness(e.target.value)}
                        style={{ cursor: 'pointer' }}
                      />
                      <span>Right-handed 🖐️</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer' }}>
                      <input
                        type="radio"
                        name="handedness"
                        value="l"
                        checked={registerHandedness === 'l'}
                        onChange={(e) => setRegisterHandedness(e.target.value)}
                        style={{ cursor: 'pointer' }}
                      />
                      <span>Left-handed 🖑️</span>
                    </label>
                  </div>
                </div>

                {registerError && <div className="error">{registerError}</div>}
                <button type="submit">Register</button>
                <div style={{ textAlign: 'center', marginTop: '15px' }}>
                  <button type="button" onClick={() => { setShowRegister(false); setRegisterError(''); }} style={{ background: '#95a5a6' }}>
                    Back to Login
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    );
  }

// ═══════════════════════════════════════════════════════
  // COACH VIEW - WITH 3 TABS (STUDENTS, SAVED NOTES, ANALYTICS)
  // ═══════════════════════════════════════════════════════
  if (user.role === 'coach' || user.role === 'headcoach') {
    // Refresh in place: fade out, reload the data, rebuild the screen (every tab fetches again, the same section comes back), fade in
    coachReloadRef.current = async () => {
      const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
      setCoachContentFading(true);
      await wait(240);
      try { sessionStorage.setItem('cpResumeCoachTab', '1'); } catch (e) { /* ignore */ }
      setCoachContentKey((k) => k + 1);
      window.dispatchEvent(new CustomEvent('cpRefreshData'));
      loadPlayers();
      loadLessonNotes();
      if (refreshCalendarRef.current) refreshCalendarRef.current();
      await wait(450);
      setCoachContentFading(false);
    };
    return (
      <div className="App" style={{
        // Pinned to the screen (not 100vh): on the iPhone 100vh is taller than the visible area, which let the page slide
        // up and down under the finger and opened a gap above the header
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}>

        {/* Connection Error Banner */}
        {connectionError && (
          <div style={{
            padding: '12px 16px',
            background: '#e74c3c',
            color: 'white',
            fontSize: '13px',
            fontWeight: 'bold',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            zIndex: 999
          }}>
            <span>⚠️ {connectionError}</span>
            <button
              onClick={() => setConnectionError(null)}
              style={{
                background: 'none',
                border: 'none',
                color: 'white',
                cursor: 'pointer',
                fontSize: '16px',
                padding: 0
              }}
            >
              ✕
            </button>
          </div>
        )}

        {/* HEADER - COLUMN LAYOUT FOR TWO ROWS */}
        <div className="header" style={{
          flexShrink: 0,
          position: 'relative',
          zIndex: 100,
          paddingBottom: '8px',
          paddingLeft: '12px',
          paddingRight: '12px',
          display: 'flex',
          flexDirection: 'column',
          gap: '12px'
        }}>
          {/* ROW 1: Title | Refresh | Name | Logout */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
            minHeight: '40px'
          }}>
            {/* LEFT: app icon */}
            <img src="/icons/icon-192.png" alt="CoachPad" width="44" height="44" style={{ width: 44, height: 44, borderRadius: 12, display: "block", flexShrink: 0, boxShadow: "0 2px 8px rgba(0,0,0,0.35)" }} />
            
            {/* RIGHT: Buttons */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              flexShrink: 0,
              flexWrap: 'nowrap'
            }}>

              {/* User Name */}
              <span className="user-name" style={{ 
                color: 'white',
                fontSize: '13px',
                fontWeight: '500',
                whiteSpace: 'nowrap',
                marginLeft: '8px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: '100px'
              }}>
                {user.name}
              </span>

              {/* Settings Button */}
              <button
                onClick={() => setActiveTab('settings')}
                title="Account Settings"
                style={{
                  padding: '6px 12px',
                  background: 'rgba(255, 255, 255, 0.2)',
                  color: 'white',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: '600',
                  transition: 'all 0.2s ease',
                  whiteSpace: 'nowrap',
                  minHeight: '32px',
                  marginLeft: '8px'
                }}
              >
                ⚙️ Settings
              </button>

            </div>
          </div>

          {/* ROW 2: Students / Saved Notes / Analytics Toggle */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            width: '100%'
          }}>
            <div style={{
              display: 'flex',
              background: 'rgba(255, 255, 255, 0.15)',
              borderRadius: '10px',
              padding: '3px',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              width: '100%',
              maxWidth: '600px',
              overflowX: 'auto',
              WebkitOverflowScrolling: 'touch'
            }}>
              {/* Students Tab */}
              <button
                onClick={() => setActiveTab('students')}
                style={{
                  flex: 1,
                  padding: '8px 12px',
                  background: activeTab === 'students' 
                    ? 'rgba(255, 255, 255, 0.95)' 
                    : 'transparent',
                  color: activeTab === 'students' ? '#2f4ba2' : 'white',
                  border: 'none',
                  borderRadius: '7px',
                  cursor: 'pointer',
                  fontWeight: '700',
                  fontSize: '13px',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '4px',
                  whiteSpace: 'nowrap',
                  minHeight: '38px'
                }}
              >
                <span style={{ fontSize: '16px' }}>👥</span>
                <span className="tab-text">Students</span>
              </button>

              {/* Saved Notes Tab */}
              <button
                onClick={() => setActiveTab('saved-notes')}
                style={{
                  flex: 1,
                  padding: '8px 12px',
                  background: activeTab === 'saved-notes' 
                    ? 'rgba(255, 255, 255, 0.95)' 
                    : 'transparent',
                  color: activeTab === 'saved-notes' ? '#2f4ba2' : 'white',
                  border: 'none',
                  borderRadius: '7px',
                  cursor: 'pointer',
                  fontWeight: '700',
                  fontSize: '13px',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '4px',
                  position: 'relative',
                  whiteSpace: 'nowrap',
                  minHeight: '38px'
                }}
              >
                <span style={{ fontSize: '16px' }}>📋</span>
                <span className="tab-text">Notes</span>
                {savedNotesCount > 0 && (
                  <span style={{
                    position: 'absolute',
                    top: '-4px',
                    right: '-4px',
                    background: '#e74c3c',
                    color: 'white',
                    borderRadius: '8px',
                    padding: '2px 5px',
                    fontSize: '10px',
                    fontWeight: '700',
                    minWidth: '16px',
                    textAlign: 'center',
                    boxShadow: '0 2px 6px rgba(231, 76, 60, 0.4)',
                    lineHeight: '1.2'
                  }}>
                    {savedNotesCount}
                  </span>
                )}
              </button>

              {/* Manager Tab - headcoach only: see each coach's schedule */}
              {user.role === 'headcoach' && (
                <button
                  onClick={() => setActiveTab('manager')}
                  style={{
                    flex: 1,
                    padding: '8px 12px',
                    background: activeTab === 'manager'
                      ? 'rgba(255, 255, 255, 0.95)'
                      : 'transparent',
                    color: activeTab === 'manager' ? '#2f4ba2' : 'white',
                    border: 'none',
                    borderRadius: '7px',
                    cursor: 'pointer',
                    fontWeight: '700',
                    fontSize: '13px',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '4px',
                    whiteSpace: 'nowrap',
                    minHeight: '38px'
                  }}
                >
                  <span style={{ fontSize: '16px' }}>🗓️</span>
                  <span className="tab-text">Manager</span>
                </button>
              )}

              {/* Tasks Tab - coach only: task lists from the headcoach, answered with a tick / cross / ? */}
              {user.role === 'coach' && (
                <button
                  onClick={() => setActiveTab('messages')}
                  style={{
                    flex: 1,
                    padding: '8px 12px',
                    background: activeTab === 'messages'
                      ? 'rgba(255, 255, 255, 0.95)'
                      : 'transparent',
                    color: activeTab === 'messages' ? '#2f4ba2' : 'white',
                    border: 'none',
                    borderRadius: '7px',
                    cursor: 'pointer',
                    fontWeight: '700',
                    fontSize: '13px',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '4px',
                    position: 'relative',
                    whiteSpace: 'nowrap',
                    minHeight: '38px'
                  }}
                >
                  <span style={{ fontSize: '16px' }}>☑️</span>
                  <span className="tab-text">Tasks</span>
                  {unansweredMessages > 0 && (
                    <span style={{
                      position: 'absolute',
                      top: '-4px',
                      right: '-4px',
                      background: '#e74c3c',
                      color: 'white',
                      borderRadius: '8px',
                      padding: '2px 5px',
                      fontSize: '10px',
                      fontWeight: '700',
                      minWidth: '16px',
                      textAlign: 'center',
                      boxShadow: '0 2px 6px rgba(231, 76, 60, 0.4)',
                      lineHeight: '1.2'
                    }}>
                      {unansweredMessages}
                    </span>
                  )}
                </button>
              )}

              {/* Import Tab - Playtomic booking exports (CSV) into the calendar */}
              <button
                onClick={() => setActiveTab('import')}
                style={{
                  flex: '1 0 auto',
                  padding: '8px 12px',
                  background: activeTab === 'import'
                    ? 'rgba(255, 255, 255, 0.95)'
                    : 'transparent',
                  color: activeTab === 'import' ? '#2f4ba2' : 'white',
                  border: 'none',
                  borderRadius: '7px',
                  cursor: 'pointer',
                  fontWeight: '700',
                  fontSize: '13px',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '4px',
                  whiteSpace: 'nowrap',
                  minHeight: '38px'
                }}
              >
                <span style={{ fontSize: '16px' }}>📥</span>
                <span className="tab-text">Import</span>
              </button>

              {/* Analytics Tab */}
              <button
                onClick={() => setActiveTab('analytics')}
                style={{
                  flex: 1,
                  padding: '8px 12px',
                  background: activeTab === 'analytics' 
                    ? 'rgba(255, 255, 255, 0.95)' 
                    : 'transparent',
                  color: activeTab === 'analytics' ? '#2f4ba2' : 'white',
                  border: 'none',
                  borderRadius: '7px',
                  cursor: 'pointer',
                  fontWeight: '700',
                  fontSize: '13px',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '4px',
                  whiteSpace: 'nowrap',
                  minHeight: '38px'
                }}
              >
                <span style={{ fontSize: '16px' }}>📊</span>
                <span className="tab-text">Analytics</span>
              </button>
            </div>
          </div>
        </div>

        {/* Pull-down refresh indicator (same as the player view) */}
        <div aria-hidden="true" style={{ position: "fixed", left: 0, right: 0, top: 96, display: "flex", justifyContent: "center", zIndex: 500, pointerEvents: "none", transform: "translateY(" + (coachPull.pull - 36) + "px)", opacity: coachPull.refreshing || coachPull.ready ? 1 : Math.min(0.7, coachPull.progress * 1.4), transition: coachPull.progress > 0 && !coachPull.refreshing ? "none" : "transform 0.35s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.3s ease-out", willChange: "transform, opacity" }}>
          <div className={coachPull.refreshing ? "pull-spinner pull-spinner--spin" : "pull-spinner"} style={{ transform: coachPull.refreshing ? undefined : "rotate(" + coachPull.progress * 300 + "deg) scale(" + (coachPull.ready ? 1.12 : 0.8 + Math.min(coachPull.progress, 1) * 0.2) + ")", transition: coachPull.progress > 0 && !coachPull.refreshing ? "transform 0.12s linear" : "transform 0.3s ease-out" }} />
        </div>

        {/* Tab Content */}
        <div ref={setCoachScrollEl} style={{
          flex: 1,
          overflow: 'auto',
          overscrollBehaviorY: 'contain',
          WebkitOverflowScrolling: 'touch',
          background: '#f5f5f5'
        }}>
        <div key={coachContentKey} style={{ minHeight: '100%', opacity: coachContentFading ? 0 : 1, transform: coachContentFading ? 'translateY(6px)' : 'none', transition: 'opacity 0.24s ease, transform 0.24s ease' }}>
          {activeTab === 'students' ? (
          <CoachView
              user={user}
              viewingStudent={viewingStudent}
              setViewingStudent={setViewingStudent}
              players={players}
              setPlayers={setPlayers}
              filteredPlayers={filteredPlayers}
              setFilteredPlayers={setFilteredPlayers}
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              showAddStudent={showAddStudent}
              setShowAddStudent={setShowAddStudent}
              newStudentEmail={newStudentEmail}
              setNewStudentEmail={setNewStudentEmail}
              searchSuggestions={searchSuggestions}
              setSearchSuggestions={setSearchSuggestions}
              showSuggestions={showSuggestions}
              setShowSuggestions={setShowSuggestions}
              showModal={showModal}
              setShowModal={setShowModal}
              selectedLesson={selectedLesson}
              setSelectedLesson={setSelectedLesson}
              lessonNote={lessonNote}
              setLessonNote={setLessonNote}
              lessonNotes={lessonNotes}
              modalConfig={modalConfig}
              setModalConfig={setModalConfig}
              showSuccessToast={showSuccessToast}
              setShowSuccessToast={setShowSuccessToast}
              refreshCalendarRef={refreshCalendarRef}
              scrollKey={scrollKey}
              setScrollKey={setScrollKey}
              editingNoteId={editingNoteId}
              setEditingNoteId={setEditingNoteId}
              editNoteText={editNoteText}
              setEditNoteText={setEditNoteText}
              newNote={newNote}
              setNewNote={setNewNote}
              newNoteBadge={newNoteBadge}
              setNewNoteBadge={setNewNoteBadge}
              lessonNotePhotos={lessonNotePhotos}
              setLessonNotePhotos={setLessonNotePhotos}
              newNoteType={newNoteType}
              setNewNoteType={setNewNoteType}
              loadingPlayers={loadingPlayers}
              handleLogout={handleLogout}
              loadPlayers={loadPlayers}
              loadLessonNotes={loadLessonNotes}
              syncBufferLessons={syncBufferLessons}
              handleBackToList={handleBackToList}
              handleViewStudent={handleViewStudent}
              handleRemoveStudent={handleRemoveStudent}
              handleAddStudent={handleAddStudent}
              handleSearchPlayers={handleSearchPlayers}
              handleSelectSuggestion={handleSelectSuggestion}
              handleLessonClick={handleLessonClick}
              handleSaveLessonNote={handleSaveLessonNote}
              handleStartEdit={handleStartEdit}
              handleCancelEdit={handleCancelEdit}
              closeModal={closeModal}
              showAlertModal={showAlertModal}
              formatDateTime={formatDateTime}
              refreshPlayersAfterDrop={refreshPlayersAfterDrop}
            />
          ) : activeTab === 'saved-notes' ? (
            <ErrorBoundary>
              <SavedNotes
                allStudents={players || []}
                formatDateTime={formatDateTime}
                showAlertModal={showAlertModal}
                userId={user?._id}
                onPocketNoteAssigned={refreshPlayersAfterPocketAssignment}
                pocketEnabled={pocketEnabledFor(user?.role)}
              />
            </ErrorBoundary>
          ) : activeTab === 'import' ? (
            <ErrorBoundary>
              <PlaytomicImportTab />
            </ErrorBoundary>
          ) : activeTab === 'messages' && user.role === 'coach' ? (
            <ErrorBoundary>
              <CoachTasks onUnansweredChange={setUnansweredMessages} />
            </ErrorBoundary>
          ) : activeTab === 'manager' && user.role === 'headcoach' ? (
            <ErrorBoundary>
              <div style={{ background: 'white', borderRadius: '16px', padding: '20px', maxWidth: '600px', margin: '16px auto' }}>
                <CoachCalendarsCard alwaysShow />
              </div>
              <div style={{ background: 'white', borderRadius: '16px', padding: '20px', maxWidth: '600px', margin: '16px auto' }}>
                <HeadcoachTasksCard />
              </div>
            </ErrorBoundary>
          ) : activeTab === 'settings' ? (
            <ErrorBoundary>
              <SettingsPage
                user={user}
                onLogout={handleLogout}
                onBack={() => setActiveTab('students')}
              />
            </ErrorBoundary>
          ) : (
            <ErrorBoundary>
              <AnalyticsDashboard
                formatDateTime={formatDateTime}
                userId={user?._id}
                pocketEnabled={pocketEnabledFor(user?.role)}
                isHeadcoach={user?.role === 'headcoach'}
              />
            </ErrorBoundary>
          )}
        </div>
        </div>

        {/* Responsive CSS */}
        <style>{`
          @media (max-width: 480px) {
            .refresh-text {
              display: none !important;
            }
            h1 {
              font-size: 16px !important;
            }
          }

          @media (max-width: 380px) {
            .user-name {
              display: none !important;
            }
          }

          @media (max-width: 320px) {
            .tab-text {
              display: none !important;
            }
          }
        `}</style>
      <InstallPrompt />
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════
  // PLAYER VIEW (also used for managers with manager tab)
  // ═══════════════════════════════════════════════════════
  if (user.role === 'player' || user.role === 'manager') {
    return (
      <>
      <MaintenanceScreen />
      {/* Connection Error Banner */}
      {connectionError && (
        <div style={{
          padding: '12px 16px',
          background: '#e74c3c',
          color: 'white',
          fontSize: '13px',
          fontWeight: 'bold',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          zIndex: 999,
          position: 'relative'
        }}>
          <span>⚠️ {connectionError}</span>
          <button
            onClick={() => setConnectionError(null)}
            style={{
              background: 'none',
              border: 'none',
              color: 'white',
              cursor: 'pointer',
              fontSize: '16px',
              padding: 0
            }}
          >
            ✕
          </button>
        </div>
      )}
      {playerShowSettings ? (
        <SettingsPage
          user={user}
          onLogout={handleLogout}
          onBack={() => setPlayerShowSettings(false)}
        />
      ) : (
        <PlayerView
          user={user}
          myNotes={myNotes}
          loading={loading}
          showNotePopup={showNotePopup}
          setShowNotePopup={setShowNotePopup}
          popupNote={popupNote}
          setPopupNote={setPopupNote}
          lastSeenNoteId={lastSeenNoteId}
          setLastSeenNoteId={setLastSeenNoteId}
          readNotes={readNotes}
          markAsRead={markAsRead}
          starredNotes={starredNotes}
          toggleStar={toggleStar}
          showMilestoneCelebration={showMilestoneCelebration}
          setShowMilestoneCelebration={setShowMilestoneCelebration}
          milestoneData={milestoneData}
          showSuccessToast={showSuccessToast}
          loadingNotes={loadingNotes}
          handleLogout={handleLogout}
          onSettingsClick={() => setPlayerShowSettings(true)}
        />
      )}
      <SkillUpdateModal
        isOpen={showSkillUpdateModal}
        skillData={skillUpdateData}
        onClose={() => setShowSkillUpdateModal(false)}
      />
      <InstallPrompt />
      <PushNudge onOpenSettings={() => setPlayerShowSettings(true)} />
      </>
    );
  }

  return (
    <>
      <MaintenanceScreen />
      {null}
    </>
  );
}

// Outer wrapper with AppStatusProvider
function App() {
  return (
    <AppStatusProvider>
      <UpdateBanner />
      <InAppNotification />
      <AppContent />
    </AppStatusProvider>
  );
}

export default App;